/**
 * CR-6385: Concessions master switch.
 *
 * The one control that decides whether this tenant runs the concession /
 * production-permit workflow at all: the Quality tab, the supplier's Raise
 * Concession button on a PO line, the list, and every concession figure.
 *
 * Default is OFF — a tenant gets concessions because an administrator turned
 * them on here, never by inheriting them. Turning it off hides the feature and
 * refuses new work; it does not delete anything already raised.
 *
 * Stored in `feature_flags.concessions_enabled`. A Helix operator writes the
 * platform default; a client admin's save is routed to their own tenant's
 * override (see `core.admin_setup_settings`), and either way the settings
 * endpoint records who changed it and when.
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Loader2, FileWarning } from 'lucide-react';
import { platformSettingsAPI } from '../../../lib/api';
import { toast } from 'sonner';

export function ConcessionFeatureSection() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  // The full feature_flags dict is kept so a toggle save doesn't drop siblings.
  const [featureFlags, setFeatureFlags] = useState<Record<string, unknown>>({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await platformSettingsAPI.getSettings();
        const flags = data.feature_flags ?? {};
        setFeatureFlags(flags);
        setEnabled(!!flags.concessions_enabled);
      } catch (err: unknown) {
        console.error('Error loading Concessions setting:', err);
        toast.error('Failed to load the Concessions setting');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleToggle = async (checked: boolean) => {
    const previous = enabled;
    setEnabled(checked);
    setSaving(true);
    try {
      const nextFlags = { ...featureFlags, concessions_enabled: checked };
      await platformSettingsAPI.patchSettings({ feature_flags: nextFlags });
      setFeatureFlags(nextFlags);
      toast.success(`Concessions: ${checked ? 'ON' : 'OFF'}`);
    } catch (err: unknown) {
      console.error('Error saving Concessions setting:', err);
      toast.error('Failed to save the Concessions setting');
      setEnabled(previous);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileWarning className="h-5 w-5" />
          Concessions / Production Permits
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Master switch for the concession workflow. Controls the Quality tab, the
          supplier's Raise Concession and Request Production Permit buttons on a PO
          line, the concession list, and every concession figure — all from this one
          setting, so they can never disagree.
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between rounded-md border border-input bg-background px-4 py-3">
          <div className="space-y-1 pr-3">
            <Label htmlFor="concessions-enabled" className="text-sm font-medium">
              Enable Concessions
            </Label>
            <p className="text-xs text-muted-foreground">
              {enabled
                ? 'ON — suppliers can raise concessions and production permits against PO lines.'
                : 'OFF — the workflow is hidden and new concessions are refused. Anything already raised is kept, not deleted.'}
            </p>
          </div>
          <Switch
            id="concessions-enabled"
            checked={enabled}
            onCheckedChange={handleToggle}
            disabled={saving}
          />
        </div>
      </CardContent>
    </Card>
  );
}
